import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckCircle2, EyeIcon, ThumbsUpIcon, ShieldCheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricing } from '../contexts/PricingContext';
import { ContactFormPopup } from './ContactFormPopup';
export function HeroVariant52() {
  const {
    language
  } = useLanguage();
  const {
    price
  } = usePricing();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const content = {
    ru: {
      title1: 'Сделаем сайт для вас ',
      titleFree: 'бесплатно.',
      title2: `Обслуживание — ${price}/мес.`,
      step1: 'Мы делаем сайт за свой счёт.',
      step2: 'Вы смотрите, просите правки, утверждаете.',
      step3: `Когда всё нравится — платите ${price} в месяц за хостинг и поддержку.`,
      step4: 'Не понравится — просто уходите. Без оплаты.',
      cta: 'Получить бесплатный макет'
    },
    ua: {
      title1: 'Зробимо сайт для вас ',
      titleFree: 'безкоштовно.',
      title2: `Обслуговування — ${price}/міс.`,
      step1: 'Ми робимо сайт за свій рахунок.',
      step2: 'Ви дивитеся, просите правки, затверджуєте.',
      step3: `Коли все подобається — платите ${price} на місяць за хостинг та підтримку.`,
      step4: 'Не сподобається — просто йдете. Без оплати.',
      cta: 'Отримати безкоштовний макет'
    },
    en: {
      title1: 'We build your website ',
      titleFree: 'for free.',
      title2: `Maintenance — ${price}/mo.`,
      step1: 'We build the site at our expense.',
      step2: 'You review, request edits, and approve.',
      step3: `When you love it, you pay ${price}/month for hosting and support.`,
      step4: "Don't like it? Just walk away. No charge.",
      cta: 'Get a free mockup'
    }
  }[language] || content.ru;
  return <>
      <section className="relative min-h-screen w-full overflow-hidden pt-32 pb-20 flex items-center" style={{
      backgroundColor: 'var(--bg-primary)'
    }} data-id="element-799">
        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col lg:flex-row items-center justify-between gap-12 px-4" data-id="element-800">
          {/* Left Column - The Checklist */}
          <div className="w-full lg:w-1/2 text-left flex flex-col items-start" data-id="element-801">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-4 text-4xl md:text-5xl lg:text-[3.5rem] font-black leading-[1.1] tracking-tight text-gray-900" data-id="element-802">
              {content.title1}
              <span className="text-blue-600" data-id="element-803">{content.titleFree}</span>
            </motion.h1>

            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="mb-8 text-2xl md:text-3xl font-bold text-gray-500" data-id="element-804">
              {content.title2}
            </motion.h2>

            {/* Scannable Checklist instead of a paragraph */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2
          }} className="space-y-4 mb-10 max-w-lg" data-id="element-805">
              <div className="flex items-start gap-3" data-id="element-806">
                <div className="mt-1 bg-blue-100 p-1.5 rounded-full text-blue-600 shrink-0" data-id="element-807">
                  <CheckCircle2 className="w-4 h-4" data-id="element-808" />
                </div>
                <p className="text-gray-700 font-medium text-lg" data-id="element-809">
                  {content.step1}
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-810">
                <div className="mt-1 bg-blue-100 p-1.5 rounded-full text-blue-600 shrink-0" data-id="element-811">
                  <EyeIcon className="w-4 h-4" data-id="element-812" />
                </div>
                <p className="text-gray-700 font-medium text-lg" data-id="element-813">
                  {content.step2}
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-814">
                <div className="mt-1 bg-blue-100 p-1.5 rounded-full text-blue-600 shrink-0" data-id="element-815">
                  <ThumbsUpIcon className="w-4 h-4" data-id="element-816" />
                </div>
                <p className="text-gray-700 font-medium text-lg" data-id="element-817">
                  {content.step3}
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-818">
                <div className="mt-1 bg-green-100 p-1.5 rounded-full text-green-600 shrink-0" data-id="element-819">
                  <ShieldCheckIcon className="w-4 h-4" data-id="element-820" />
                </div>
                <p className="text-gray-900 font-bold text-lg" data-id="element-821">
                  {content.step4}
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-822">
              <button onClick={() => setIsPopupOpen(true)} className="group flex items-center justify-center gap-2 rounded-2xl px-10 py-5 font-bold text-lg transition-all hover:scale-105 bg-gray-900 text-white shadow-xl" data-id="element-823">
                {content.cta}
                <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-824" />
              </button>
            </motion.div>
          </div>

          {/* Right Column - Clean Video */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} className="w-full lg:w-1/2 relative mt-12 lg:mt-0" data-id="element-825">
            <div className="relative w-full max-w-2xl mx-auto rounded-3xl overflow-hidden shadow-2xl border border-gray-100" data-id="element-826">
              <video src="https://cdn.magicpatterns.com/uploads/vrXe21wkVqS62d5BzpZ9eJ/original-2de319f4767c5a0d4acf4f107b938504.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-827" />
            </div>
          </motion.div>
        </div>
      </section>
      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-828" />
    </>;
}